'use client';
import { useRef, useCallback, useEffect } from 'react';
import { useReducedMotion } from '../useReducedMotion/useReducedMotion.js';
import { getScrollStart } from './utils/getScrollStart.js';
import { getRelativePosition } from './utils/getRelativePosition.js';
import { setScrollParam } from './utils/setScrollParam.js';
import { useWindowEvent } from '../useWindowEvent/useWindowEvent.js';

const easeInOutQuad = (t)=>t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
const useScrollIntoView = ({ duration = 300, axis = 'y', onScrollFinish, offset = 0, cancelable = true, isList = false } = {})=>{
    const frameID = useRef(0);
    const startTime = useRef(0);
    const shouldStop = useRef(false);
    const scrollableRef = useRef(null);
    const targetRef = useRef(null);
    const reducedMotion = useReducedMotion();
    const cancel = ()=>{
        if (frameID.current) {
            cancelAnimationFrame(frameID.current);
        }
    };
    const scrollIntoView = useCallback(({ alignment = 'start' } = {})=>{
        shouldStop.current = false;
        if (frameID.current) {
            cancel();
        }
        var _getScrollStart;
        const start = (_getScrollStart = getScrollStart({
            parent: scrollableRef.current,
            axis
        })) != null ? _getScrollStart : 0;
        const change = getRelativePosition({
            parent: scrollableRef.current,
            target: targetRef.current,
            axis,
            alignment,
            offset,
            isList
        }) - (scrollableRef.current ? 0 : start);
        function animateScroll() {
            if (startTime.current === 0) {
                startTime.current = performance.now();
            }
            const now = performance.now();
            const elapsed = now - startTime.current;
            const t = reducedMotion || duration === 0 ? 1 : elapsed / duration;
            const distance = start + change * easeInOutQuad(t);
            setScrollParam({
                parent: scrollableRef.current,
                axis,
                distance
            });
            if (!shouldStop.current && t < 1) {
                frameID.current = requestAnimationFrame(animateScroll);
            } else {
                typeof onScrollFinish === 'function' && onScrollFinish();
                startTime.current = 0;
                frameID.current = 0;
                cancel();
            }
        }
        animateScroll();
    }, [
        axis,
        duration,
        isList,
        offset,
        onScrollFinish,
        reducedMotion
    ]);
    const handleStop = ()=>{
        if (cancelable) {
            shouldStop.current = true;
        }
    };
    useWindowEvent('wheel', handleStop, {
        passive: true
    });
    useWindowEvent('touchmove', handleStop, {
        passive: true
    });
    useEffect(()=>cancel, []);
    return {
        scrollableRef,
        targetRef: targetRef,
        scrollIntoView,
        cancel
    };
};

export { useScrollIntoView };
//# sourceMappingURL=useScrollIntoVIew.js.map
